/**
 * CustomerPicker — searchable customer selector for the composer.
 *
 * Visual: a Card-styled summary tile that doubles as a button.
 * Closed state shows the picked customer's name + GSTIN + state +
 * payment terms hint (mirrors the prototype's compact "customer
 * card"). Empty state is a `Select customer…` placeholder.
 *
 * Open state opens a Dialog with:
 *   - search input (filters `customers.list` by `q`)
 *   - scrollable list of matches
 *   - "+ Add new customer" footer that opens `/sales/customers/new` in a
 *     new tab (the dedicated composer handles GSTIN-already-claimed,
 *     address validation, etc.). Mirrors S03's "quick add" pattern
 *     from the screen spec §5.1.2.
 *
 * Why Dialog (not Popover): the existing UI primitives are Dialog,
 * DropdownMenu, AlertDialog, ConfirmDialog — no Popover. Dialog is
 * the closest fit for "modal pick + sub-form" and the prototype
 * shows the picker centred over the page.
 *
 * @see docs/spec/11-new-invoice-screen-spec.md §5 — customer + billing
 *      context.
 */
import { useEffect, useState } from "react";
import { Search, UserPlus, X, Pencil } from "lucide-react";
import {
  Alert,
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Input,
  StatusPill,
} from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";
import type { customers as customersContract } from "@flint/api-contract";
import type { z } from "zod";

/**
 * The customer fields the picker and its consumers (invoice + proforma
 * composers, the invoice list filter bar) actually read.
 *
 * Derived from `listRow` so the field types can't drift, but deliberately
 * narrower: the full row carries six per-customer aggregates
 * (`outstandingPaise`, `ytdInvoicedPaise`, `invoiceCount`, …) that none of
 * those consumers render. Requiring them forced every caller adapting a
 * `customers.get` / `invoices.get` payload to fabricate zeros.
 */
export type CustomerSummary = Pick<
  z.infer<typeof customersContract.listRow>,
  | "id"
  | "name"
  | "contactName"
  | "gstin"
  | "phone"
  | "state"
  | "city"
  | "paymentTerms"
  | "creditLimitPaise"
>;

export interface CustomerPickerProps {
  /** Currently picked customer; `null` until the operator picks one. */
  selected: CustomerSummary | null;
  onSelect: (customer: CustomerSummary) => void;
  /** Optional disabled state (e.g. while the issue mutation is in flight). */
  disabled?: boolean;
}

export function CustomerPicker({ selected, onSelect, disabled }: CustomerPickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="w-full text-left rounded-md border border-line bg-bg hover:bg-raised disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {selected ? <SelectedCustomerRow customer={selected} /> : <EmptyCustomerRow />}
      </button>

      <CustomerPickerDialog
        open={open}
        onOpenChange={setOpen}
        onPick={(c) => {
          onSelect(c);
          setOpen(false);
        }}
      />
    </>
  );
}

function SelectedCustomerRow({ customer }: { customer: CustomerSummary }) {
  // Sub-line mirrors the prototype's S12 customer-card:
  //   "<GSTIN> · <City>, <State> · contact <phone>"
  // GSTIN gets stronger weight; the rest stay muted. The "paid-history"
  // line ("4 of 4 paid early") in the prototype needs receipts data —
  // it lands with the V1.x receipts vertical, so we show the default
  // payment terms instead until then.
  const locationParts = [customer.city, customer.state].filter(Boolean);
  return (
    <div className="flex items-start gap-3 p-4">
      <span
        aria-hidden
        className="inline-grid place-items-center h-10 w-10 rounded-md bg-sky-50 text-sky-700 font-semibold text-sm shrink-0"
      >
        {initials(customer.name)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <strong className="text-sm text-ink truncate">{customer.name}</strong>
          {customer.gstin ? (
            <StatusPill tone="ok" size="sm">
              GSTIN verified
            </StatusPill>
          ) : null}
        </div>
        <p className="mt-0.5 text-sm text-muted">
          {customer.gstin ? (
            <span className="font-mono font-medium text-ink-2">{customer.gstin}</span>
          ) : (
            <span className="text-muted">No GSTIN on file</span>
          )}
          {locationParts.length > 0 ? <> · {locationParts.join(", ")}</> : null}
          {customer.phone ? (
            <>
              {" "}
              · contact <span className="font-mono">{customer.phone}</span>
            </>
          ) : null}
        </p>
        {customer.paymentTerms ? (
          <p className="mt-1 text-sm text-muted">
            Default terms: <span className="text-ink-2">{customer.paymentTerms}</span>
          </p>
        ) : null}
      </div>
      <span
        aria-hidden
        className="inline-flex shrink-0 items-center gap-1 self-start rounded-md border border-line bg-paper px-2 py-1 text-xs font-medium text-ink-2"
      >
        <Pencil size={12} />
        Change
      </span>
    </div>
  );
}

function EmptyCustomerRow() {
  return (
    <div className="flex items-center gap-3 p-4 text-sm text-muted">
      <span
        aria-hidden
        className="inline-grid place-items-center h-10 w-10 rounded-md bg-raised text-muted shrink-0"
      >
        <UserPlus size={18} />
      </span>
      <span>Select customer…</span>
    </div>
  );
}

interface CustomerPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (customer: CustomerSummary) => void;
}

function CustomerPickerDialog({ open, onOpenChange, onPick }: CustomerPickerDialogProps) {
  const [search, setSearch] = useState("");

  // Reset search on close so the next open starts fresh.
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  // Debounce the search by 200ms — avoids one query per keystroke
  // without making the list feel laggy.
  const debounced = useDebounced(search, 200);

  const list = trpc.customers.list.useQuery({
    limit: 50,
    tab: "all",
    ...(debounced.trim() ? { q: debounced.trim() } : {}),
  });

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] p-0 gap-0">
        <DialogHeader className="p-4 border-b border-line">
          <DialogTitle>Select customer</DialogTitle>
          <DialogDescription>
            Search by name or GSTIN. Add a new customer if they aren&apos;t in the list.
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 border-b border-line">
          <div className="relative">
            <Search
              size={14}
              aria-hidden
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
            />
            <Input
              autoFocus
              type="search"
              placeholder="Customer name or GSTIN…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-ink-2 p-1"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            ) : null}
          </div>
        </div>

        <div className="max-h-[320px] overflow-y-auto">
          {list.isLoading ? (
            <p className="px-4 py-6 text-sm text-muted">Loading customers…</p>
          ) : list.error ? (
            <div className="p-4">
              <Alert
                intent="bad"
                title="Couldn't load customers"
                description={list.error.message}
              />
            </div>
          ) : list.data && list.data.items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted">
              {debounced
                ? `No customers match "${debounced}".`
                : "No customers yet — add one below."}
            </p>
          ) : list.data ? (
            <ul className="py-1">
              {list.data.items.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => onPick(c)}
                    className="w-full text-left px-4 py-2.5 hover:bg-raised flex items-start gap-3"
                  >
                    <span
                      aria-hidden
                      className="inline-grid place-items-center h-8 w-8 rounded-md bg-sky-50 text-sky-700 font-semibold text-sm shrink-0"
                    >
                      {initials(c.name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-ink truncate">{c.name}</div>
                      <div className="text-sm text-muted truncate">
                        {[c.gstin, c.state, c.city].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="p-3 border-t border-line bg-raised">
          <Button
            type="button"
            intent="ghost"
            size="sm"
            onClick={() => {
              // Quick add — for V1.0.x we link to the dedicated /customers/new
              // route instead of inlining the form. The dedicated page already
              // handles the GSTIN-already-claimed dialog, address validation,
              // etc. After saving, the operator returns to the composer (the
              // composer's autosave preserves their progress).
              window.open("/sales/customers/new", "_blank", "noopener");
            }}
            className="w-full justify-center"
          >
            <UserPlus size={14} />
            <span>Add new customer</span>
          </Button>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}

// ── helpers ────────────────────────────────────────────────────────

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return ((parts[0][0] ?? "") + (parts[parts.length - 1]![0] ?? "")).toUpperCase();
}

/** Tiny debounce hook — no need for a workspace-wide lib for one site. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}
